import React, { useState, useRef, useEffect } from "react";
import { Lock, ShieldCheck, Sparkles, Home as HomeIcon, Users, Heart, Facebook } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "sa_site_unlocked";
const CORRECT_PIN = "0903";

export default function DonatePinGate({ children }) {
  const [unlocked, setUnlocked] = useState(
    typeof window !== "undefined" && sessionStorage.getItem(STORAGE_KEY) === "1"
  );
  const [pin, setPin] = useState(["", "", "", ""]);
  const [shaking, setShaking] = useState(false);
  const inputs = useRef([]);

  useEffect(() => {
    if (!unlocked) inputs.current[0]?.focus();
  }, [unlocked]);

  const setDigit = (i, v) => {
    const digit = v.replace(/\D/g, "").slice(-1);
    const next = [...pin];
    next[i] = digit;
    setPin(next);
    if (digit && i < 3) inputs.current[i + 1]?.focus();
    if (next.every((d) => d)) tryUnlock(next.join(""));
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !pin[i] && i > 0) {
      inputs.current[i - 1]?.focus();
      const next = [...pin];
      next[i - 1] = "";
      setPin(next);
      e.preventDefault();
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!text) return;
    e.preventDefault();
    const next = text.padEnd(4, "").split("").slice(0, 4);
    const padded = [next[0] || "", next[1] || "", next[2] || "", next[3] || ""];
    setPin(padded);
    if (padded.every((d) => d)) tryUnlock(padded.join(""));
    else inputs.current[text.length]?.focus();
  };

  const tryUnlock = (attempt) => {
    if (attempt === CORRECT_PIN) {
      sessionStorage.setItem(STORAGE_KEY, "1");
      setUnlocked(true);
      toast.success("Unlocked. Thank you for being here.");
    } else {
      setShaking(true);
      setTimeout(() => {
        setShaking(false);
        setPin(["", "", "", ""]);
        inputs.current[0]?.focus();
      }, 500);
      toast.error("That PIN doesn't match. Please try again.");
    }
  };

  if (unlocked) return children;

  return (
    <div data-testid="donate-pin-gate" className="min-h-screen px-6 py-12 md:py-16 relative overflow-hidden bg-[#FDFBF7]">
      {/* Decorative background */}
      <div className="absolute inset-0 puzzle-dots opacity-30" aria-hidden />
      <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-[#2196F3]/15 blur-3xl" aria-hidden />
      <div className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full bg-[#FFB300]/15 blur-3xl" aria-hidden />

      <div className="relative max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
        {/* LEFT — Coming Soon story */}
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-black/5 px-4 py-1.5 text-xs font-semibold text-[#4B5563] uppercase tracking-[0.18em]">
            <Sparkles className="w-3.5 h-3.5 text-[#FFB300]" strokeWidth={2} />
            Coming Soon
          </div>
          <h1 className="mt-5 font-heading font-bold tracking-tighter text-[#1F2937] leading-[0.95]
                         text-4xl sm:text-5xl lg:text-6xl">
            Stars of Autism<br />
            <span className="brush-underline brush-blue">is almost here.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#4B5563] leading-relaxed max-w-xl">
            We are quietly building a permanent, sensory-friendly home for Autism families in
            <span className="text-[#1F2937] font-semibold"> Marion, Arkansas</span>. Our public launch
            is around the corner — enter the access PIN to preview the experience.
          </p>

          <div className="mt-8 grid sm:grid-cols-3 gap-3">
            {[
              { c: "#2196F3", icon: HomeIcon, t: "A permanent home", d: "Resources for families beyond the school system." },
              { c: "#FFB300", icon: Sparkles, t: "Sensory-friendly hall", d: "A joyful venue for parties every child can enjoy." },
              { c: "#4CAF50", icon: Users, t: "Community wall", d: "Every donor becomes a puzzle piece of our story." },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.t} data-testid={`coming-soon-card-${b.t.split(" ")[0].toLowerCase()}`} className="rounded-2xl bg-white border border-black/5 p-5">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                       style={{ backgroundColor: `${b.c}15`, border: `1px solid ${b.c}44` }}>
                    <Icon className="w-4 h-4" style={{ color: b.c }} strokeWidth={1.8} />
                  </div>
                  <div className="mt-3 font-heading font-semibold text-[#1F2937] text-sm">{b.t}</div>
                  <div className="mt-1 text-xs text-[#6B7280] leading-relaxed">{b.d}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <a href="https://www.facebook.com/share/1DGrahrNKh/" target="_blank" rel="noreferrer"
               data-testid="coming-soon-fb-link"
               className="inline-flex items-center gap-2 rounded-full bg-[#1F2937] hover:bg-black text-white px-5 py-2.5 text-sm font-semibold">
              <Facebook className="w-4 h-4" strokeWidth={1.8} /> Follow our story on Facebook
            </a>
            <div className="inline-flex items-center gap-2 text-xs text-[#6B7280]">
              <Heart className="w-3.5 h-3.5 text-[#E53935] fill-current" strokeWidth={0} />
              Different, not less.
            </div>
          </div>
        </div>

        {/* RIGHT — Lock card */}
        <div className={`lg:col-span-5 ${shaking ? "animate-[shake_500ms]" : ""}`}>
          <div className="w-full rounded-3xl bg-white border border-black/5 shadow-sm p-8 md:p-10">
            <img src="/images/logo.jpg" alt="Stars of Autism" className="h-16 w-16 object-contain mx-auto" />
            <div className="mt-4 w-14 h-14 rounded-2xl bg-[#1F2937] text-white flex items-center justify-center mx-auto">
              <Lock className="w-6 h-6" strokeWidth={1.6} />
            </div>
            <h2 className="mt-5 text-center font-heading text-2xl font-semibold tracking-tight text-[#1F2937]">
              Early access preview
            </h2>
            <p className="mt-2 text-center text-sm text-[#6B7280] leading-relaxed max-w-xs mx-auto">
              This site is currently private. Enter your 4-digit access PIN to continue.
            </p>

            <div className="mt-8 flex items-center justify-center gap-3" data-testid="pin-input-row" onPaste={onPaste}>
              {pin.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputs.current[i] = el)}
                  data-testid={`pin-digit-${i}`}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={d}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  className="w-12 h-14 md:w-14 md:h-16 rounded-2xl bg-[#F4F1EA] border border-black/5 text-center font-heading text-2xl md:text-3xl font-bold text-[#1F2937] focus:outline-none focus:ring-2 focus:ring-[#2196F3]"
                  aria-label={`PIN digit ${i + 1}`}
                />
              ))}
            </div>

            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-[#6B7280]">
              <ShieldCheck className="w-4 h-4 text-[#4CAF50]" strokeWidth={1.6} />
              Different, not less.
            </div>

            <div className="mt-4 text-center text-xs text-[#9CA3AF]">
              Don&apos;t have the PIN? Reach out to our director Chatoria on <a href="https://www.facebook.com/share/1DGrahrNKh/" target="_blank" rel="noreferrer" className="text-[#2196F3] hover:underline" data-testid="pin-contact-link">Facebook</a>.
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-10px); }
          30% { transform: translateX(10px); }
          45% { transform: translateX(-8px); }
          60% { transform: translateX(8px); }
          75% { transform: translateX(-4px); }
          90% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
}
